import React from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  addAllBoard,
  addBoard,
  removeBoard,
} from "../../../redux/SearchSlice";
import { useCustomSearchParams } from "../../../hooks/useCustomSearchParams";
import { useResponsive } from "../../../hooks/useResponsive";
import { useMineCards } from "../../../reactQuery/cards";
import { useUserWorkflowList } from "../../../reactQuery/workflows";
import {
  getBoardsWithCards,
  getFilteredBoards,
  getSelectAllBoardIds,
  getSelectedBoardIds,
  getVisibleBoardList,
} from "../utils/boardFilterUtils";

export function useBoardsFilterState({ isAssignToMe = false } = {}) {
  const [oldBoard, updateParams, removeSearchParam] =
    useCustomSearchParams("board");
  const [searchQuery, setSearchQuery] = React.useState("");
  const [anchorEl, setAnchorEl] = React.useState(null);
  const dispatch = useDispatch();
  const isMobile = useResponsive("down", "md");

  const activeBoard = useSelector((state) => state.activeBoard.activeBoardList);
  const checkedList = useSelector((state) => state.search.boardSearch);
  const user = useSelector((state) => state.search.myTaskUser);

  const { userWorkflows } = useUserWorkflowList();
  const genericWorkflowId = React.useMemo(
    () =>
      userWorkflows?.data?.data?.responseData?.find(
        (workflow) => workflow.is_generic
      )?.workflow_id,
    [userWorkflows]
  );

  const { data: mineCards } = useMineCards(
    user || [],
    "",
    "",
    "",
    genericWorkflowId
  );

  const boardsWithCards = React.useMemo(
    () => getBoardsWithCards(mineCards),
    [mineCards]
  );

  const activeBoardList = React.useMemo(
    () =>
      getVisibleBoardList({
        activeBoard,
        isAssignToMe,
        boardsWithCards,
      }),
    [activeBoard, boardsWithCards, isAssignToMe]
  );

  const filteredItems = React.useMemo(
    () =>
      getFilteredBoards({
        boards: activeBoardList,
        searchQuery,
        checkedList,
      }),
    [activeBoardList, checkedList, searchQuery]
  );

  const open = Boolean(anchorEl);
  const isAllSelected = React.useMemo(
    () => activeBoardList.length === checkedList.length,
    [activeBoardList.length, checkedList.length]
  );

  const syncBoards = React.useCallback(
    (nextBoardIds) => {
      if (!isMobile) {
        if (nextBoardIds.length) {
          updateParams("board", nextBoardIds);
        } else {
          removeSearchParam("board");
        }
      }

      dispatch(addAllBoard(nextBoardIds));
    },
    [dispatch, isMobile, removeSearchParam, updateParams]
  );

  const selectBoard = React.useCallback(
    (_event, boardId) => {
      const nextBoardIds = getSelectedBoardIds({
        checkedList,
        boardId,
      });

      if (checkedList.includes(boardId)) {
        dispatch(removeBoard(boardId));
      } else {
        dispatch(addBoard(boardId));
      }

      if (!isMobile) {
        if (nextBoardIds.length) {
          updateParams("board", nextBoardIds);
        } else {
          removeSearchParam("board");
        }
      }

      setSearchQuery("");
    },
    [checkedList, dispatch, isMobile, removeSearchParam, updateParams]
  );

  const selectAll = React.useCallback(() => {
    syncBoards(
      getSelectAllBoardIds({
        boards: activeBoardList,
        checkedList,
      })
    );
  }, [activeBoardList, checkedList, syncBoards]);

  const handleDeleteBoard = React.useCallback(() => {
    removeSearchParam("board");
    dispatch(addAllBoard([]));
  }, [dispatch, removeSearchParam]);

  const handleClose = React.useCallback(() => {
    setAnchorEl(null);
    setSearchQuery("");
  }, []);

  const handleButtonClick = React.useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  React.useEffect(() => {
    if (!oldBoard) {
      return;
    }

    const boardIds = oldBoard.split(",").map((value) => Number(value));
    dispatch(addAllBoard(boardIds));
  }, [dispatch, oldBoard]);

  return {
    anchorEl,
    checkedList,
    filteredItems,
    handleButtonClick,
    handleClose,
    handleDeleteBoard,
    isAllSelected,
    isMobile,
    open,
    searchQuery,
    selectAll,
    selectBoard,
    setSearchQuery,
  };
}
